import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { logObligationCompletion } from 'services/ProgrammeService'

const todayStr = () => new Date().toISOString().slice(0, 10)

const LogCompletionModal = ({ bpId, obligation, onClose, onLogged }) => {
  const [date, setDate] = useState(todayStr())
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = () => {
    if (!date || saving) return
    setSaving(true)
    logObligationCompletion(bpId, obligation.id, { completedAt: date, note: note.trim() || null })
      .then(res => {
        const satisfied = res?.data?.data?.satisfied
        if (satisfied) {
          toast.success('Logged. This obligation is now satisfied.')
        } else {
          toast.warning('Logged, but that date is outside the required interval, so this obligation is still outstanding. A more recent completion is needed.')
        }
        onLogged?.()
      })
      .catch(e => toast.error(e?.response?.data?.message || 'Could not log this as completed.'))
      .finally(() => setSaving(false))
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <Title>Mark as already done</Title>
        <Body>
          Record that <strong>{obligation.name}</strong> was already carried out. This logs a completed
          task on the matching work area. If the date falls within the obligation's required interval it
          will be marked satisfied; an older date will not satisfy it.
        </Body>

        <Label htmlFor='log-date'>When was it completed?</Label>
        <DateInput id='log-date' type='date' max={todayStr()} value={date} onChange={e => setDate(e.target.value)} data-tour-id='logCompletion.date' />

        <Label htmlFor='log-note'>Note (optional)</Label>
        <Note
          id='log-note'
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder='e.g. "Serviced by Acme on 12 May; certificate on file"'
          maxLength={1000}
          rows={3}
        />

        <Footer>
          <Cancel onClick={onClose}>Cancel</Cancel>
          <Save disabled={!date || saving} onClick={submit} data-tour-id='logCompletion.save'>{saving ? 'Saving…' : 'Mark as done'}</Save>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body
  )
}

export default LogCompletionModal

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 100001; padding: 20px;
`

const Panel = styled.div`
  background: #fff; border-radius: 14px;
  width: 100%; max-width: 460px;
  padding: 22px 24px;
  box-shadow: 0 20px 48px rgba(0,0,0,0.2);
  display: flex; flex-direction: column;
`

const Title = styled.h3`
  margin: 0 0 8px; font-size: 16px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Body = styled.p`
  margin: 0 0 16px; font-size: 13px; line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[700] || '#475569'};
`

const Label = styled.label`
  font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800] || '#6e6e78'};
  margin-bottom: 6px;
`

const DateInput = styled.input`
  font: inherit; font-size: 13.5px;
  border: 1px solid #ececec; border-radius: 10px; padding: 9px 12px;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const Note = styled.textarea`
  font: inherit; font-size: 13px;
  border: 1px solid #ececec; border-radius: 10px; padding: 10px 12px;
  resize: vertical; min-height: 60px;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px;
`

const Cancel = styled.button`
  font: inherit; background: #fff; border: 1px solid #ececec; border-radius: 8px;
  padding: 8px 16px; font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti}; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Save = styled.button`
  font: inherit; background: ${({ theme }) => theme.colors.secondary};
  color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`
